import { useParams, Link } from "react-router-dom";
import SEO from "@/components/base/SEO";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import Breadcrumb from "@/components/feature/Breadcrumb";
import ScrollToTop from "@/components/feature/ScrollToTop";
import { videoData } from "@/mocks/videos";

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const video = videoData.find((v) => v.id === Number(id));

  if (!video) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-16">
          <div className="text-center">
            <h1 className="text-2xl font-heading text-foreground-800 mb-4">内容未找到</h1>
            <Link to="/" className="text-primary-500 hover:text-primary-600 cursor-pointer">
              返回首页
            </Link>
          </div>
        </div>
        <Footer />
      </>
    );
  }

  const relatedVideos = videoData
    .filter((v) => v.id !== video.id && (v.category === video.category || v.tags.some((t) => video.tags.includes(t))))
    .slice(0, 5);

  const articleJsonLd = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Article",
        headline: video.title,
        description: video.description,
        datePublished: video.updateTime,
        dateModified: video.updateTime,
        author: {
          "@type": "Organization",
          name: "玫瑰视频",
        },
        publisher: {
          "@type": "Organization",
          name: "玫瑰视频",
          url: "https://meiguishipin.com",
        },
        mainEntityOfPage: {
          "@type": "WebPage",
          "@id": `https://meiguishipin.com/article/${video.id}`,
        },
        image: video.image,
        articleSection: video.category,
        keywords: video.tags.join(","),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "首页", item: "https://meiguishipin.com/" },
          { "@type": "ListItem", position: 2, name: video.category, item: `https://meiguishipin.com/category/${video.category}` },
          { "@type": "ListItem", position: 3, name: video.title },
        ],
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">{JSON.stringify(articleJsonLd)}</script>
      <SEO
        title={`${video.title} - 玫瑰视频高清在线观看 | ${video.source}`}
        description={`${video.title}：${video.description} 来玫瑰视频高清免费在线观看${video.title}全集，不缓冲流畅播放。`}
        keywords={`${video.title},${video.category},高清在线观看,${video.source},免费${video.category},${video.tags.join(",")}`}
        canonicalUrl={`https://meiguishipin.com/article/${video.id}`}
        ogType="article"
        ogImage={video.image}
        articleData={{
          publishedTime: video.updateTime,
          modifiedTime: video.updateTime,
          author: "玫瑰视频",
          tags: video.tags,
        }}
      />

      <Navbar />

      <main className="pt-20 pb-14">
        <div className="max-w-5xl mx-auto">
          <Breadcrumb
            items={[
              { name: "首页", path: "/" },
              { name: video.category, path: `/category/${video.category === "电视剧" ? "tv-series" : video.category === "电影" ? "movies" : video.category === "综艺" ? "variety" : video.category === "动漫" ? "anime" : video.category === "纪录片" ? "documentary" : "kids"}` },
              { name: video.title },
            ]}
          />

          <div className="px-4 md:px-6">
            <article>
              <div className="flex flex-col lg:flex-row gap-8 mb-10">
                <div className="w-full lg:w-72 flex-shrink-0">
                  <div className="aspect-[3/4] rounded-xl overflow-hidden bg-background-200">
                    <img
                      src={video.image}
                      alt={video.title}
                      className="w-full h-full object-cover object-top"
                      title={`${video.title} - ${video.category}高清在线观看`}
                    />
                  </div>
                </div>

                <div className="flex-1">
                  <h1 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-4">
                    {video.title}
                  </h1>

                  <div className="flex items-center gap-3 mb-4 flex-wrap">
                    <span className="px-2.5 py-1 bg-accent-100 text-accent-700 text-xs rounded-md font-medium">
                      {video.category}
                    </span>
                    <span className="text-sm text-foreground-500">{video.source}</span>
                    <span className="text-sm text-foreground-500">{video.year}</span>
                    <span className="flex items-center gap-1 text-sm text-accent-600">
                      <i className="ri-star-fill text-sm"></i>
                      <strong>{video.rating}</strong>
                    </span>
                    {video.episodes > 1 && (
                      <span className="text-sm text-foreground-500">共{video.episodes}集</span>
                    )}
                  </div>

                  <div className="flex items-center gap-2 mb-6 flex-wrap">
                    {video.tags.map((tag) => (
                      <span
                        key={tag}
                        className="px-2.5 py-1 bg-secondary-100 text-secondary-700 text-xs rounded-full"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>

                  <div className="bg-background-100 rounded-xl p-5 mb-6">
                    <h2 className="text-base font-semibold text-foreground-800 mb-3">剧情简介</h2>
                    <p className="text-sm text-foreground-600 leading-relaxed">
                      {video.longDescription}
                    </p>
                  </div>

                  <div className="flex items-center gap-3">
                    <button className="px-6 py-2.5 bg-primary-500 hover:bg-primary-600 text-white rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center gap-2">
                      <i className="ri-play-fill text-lg"></i>
                      立即观看
                    </button>
                    <button className="px-6 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-700 rounded-full text-sm font-medium whitespace-nowrap cursor-pointer transition-colors flex items-center gap-2">
                      <i className="ri-heart-line"></i>
                      收藏
                    </button>
                  </div>
                </div>
              </div>

              <div className="border-t border-background-200/70 pt-10">
                <h2 className="text-xl font-heading text-foreground-900 mb-6">影片详情</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="bg-background-100 rounded-xl p-4">
                    <span className="text-xs text-foreground-500">类型</span>
                    <p className="text-sm text-foreground-800 mt-1">{video.category}</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-4">
                    <span className="text-xs text-foreground-500">年份</span>
                    <p className="text-sm text-foreground-800 mt-1">{video.year}</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-4">
                    <span className="text-xs text-foreground-500">{video.episodes > 1 ? "集数" : "时长"}</span>
                    <p className="text-sm text-foreground-800 mt-1">{video.episodes > 1 ? `共${video.episodes}集` : "约120分钟"}</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-4">
                    <span className="text-xs text-foreground-500">来源平台</span>
                    <p className="text-sm text-foreground-800 mt-1">{video.source}</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-4">
                    <span className="text-xs text-foreground-500">评分</span>
                    <p className="text-sm text-foreground-800 mt-1">{video.rating} / 10</p>
                  </div>
                  <div className="bg-background-100 rounded-xl p-4">
                    <span className="text-xs text-foreground-500">更新日期</span>
                    <p className="text-sm text-foreground-800 mt-1">{video.updateTime}</p>
                  </div>
                </div>
              </div>

              {relatedVideos.length > 0 && (
                <div className="border-t border-background-200/70 pt-10 mt-10">
                  <h2 className="text-xl font-heading text-foreground-900 mb-6">相关推荐</h2>
                  <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
                    {relatedVideos.map((v) => (
                      <Link
                        key={v.id}
                        to={`/article/${v.id}`}
                        className="group cursor-pointer"
                      >
                        <div className="relative aspect-[3/4] rounded-lg overflow-hidden bg-background-200 mb-3">
                          <img
                            src={v.image}
                            alt={v.title}
                            className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                            title={`${v.title} - ${v.category}`}
                          />
                          <div className="absolute top-2 left-2">
                            <span className="px-2 py-0.5 bg-primary-500 text-white text-xs rounded-md font-medium">
                              {v.rating}
                            </span>
                          </div>
                        </div>
                        <h3 className="text-sm font-medium text-foreground-800 group-hover:text-primary-500 transition-colors line-clamp-1">
                          {v.title}
                        </h3>
                        <p className="text-xs text-foreground-500 mt-1">{v.source}</p>
                      </Link>
                    ))}
                  </div>
                </div>
              )}
            </article>
          </div>
        </div>
      </main>

      <Footer />
    </>
  );
}